import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FeedbackType, KEYS, OpenOrder } from '@pos-app/data';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ApiService, ConfigService, DialogService, ElectronService } from '../../services';
import { ClipboardService } from 'ngx-clipboard';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'pos-app-feedback',
    templateUrl: './feedback.component.html',
    styleUrls: ['./feedback.component.scss'],
    standalone: false
})
export class FeedbackComponent implements OnInit {
  @ViewChild('content') modalBox: any;
  @Input() isExternalUser: boolean;
  @Input() customerDetails: any;
  @Output() closeFeedback = new EventEmitter<void>();
  openOrder: OpenOrder;
  feedbackTypeList: FeedbackType[];
  unSubscribe$ = new Subject<void>();
  feedbackType = '';
  feedbackDetails = null;
  emailString = '';
  DATA_HELPDESK = 'https://arbcorporation.atlassian.net/servicedesk/customer/portal/2/group/44/create/141';
  SYSTEM_HELPDESK = 'https://arbcorporation.atlassian.net/servicedesk/customer/portal/2/group/43/create/140';
  version: string;
  registerId: string;
  timeStamp: string;
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();
  isElectron: boolean;
  sentToEmail: any;
  dialogServiceShown: boolean = false;

  constructor(
    private modalService: NgbModal,
    private apiService: ApiService,
    private clipboardService: ClipboardService,
    private configService: ConfigService,
    private electronService: ElectronService,
    private dialogService: DialogService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.isElectron = this.configService.AppConfig.isElectron;
    this.version = localStorage.getItem(KEYS.version);
    const cashDrawer = localStorage.getItem(KEYS.cashDrawer) ? JSON.parse(localStorage.getItem(KEYS.cashDrawer)) : null;
    this.registerId = cashDrawer ? `${cashDrawer.registerID}_${cashDrawer.branch}` : '';
    this.clipboardService.configure({ cleanUpAfterCopy: true });
    const now = moment();
    this.timeStamp = now.format('DD/MM/YYYY HH:mm');

    this.isLoadingSubject.next(true);
    combineLatest([this.apiService.getFeedbackTypes(), this.apiService.fetchUserState()])
      .pipe(
        finalize(() => this.isLoadingSubject.next(false)),
        takeUntil(this.unSubscribe$)
      )
      .subscribe(([feedbackTypes, userState]) => {
        this.isLoadingSubject.next(false);
        this.feedbackTypeList = feedbackTypes.SearchResults;
        this.openOrder = userState.OpenOrder;
        this.modalService.open(this.modalBox, { backdrop: 'static', size: 'lg' }).result.then(
          () => {
            this.closeFeedback.emit();
          },
          () => {
            this.closeFeedback.emit();
          }
        );
      });
  }

  gatherInformation() {
    // Keep this indentation for clipboard
    if (!this.feedbackType) {
      this.feedbackDetails = null; // Reset feedback details if no valid type is selected
      this.emailString = '';
      return;
    }
    this.sentToEmail = this.feedbackTypeList.find((ftype) => ftype.feedbackDescription === this.feedbackType);
    this.feedbackDetails = this.feedbackDetailsString();
    // if (this.isExternalUser) {
    const detailsForEmail = this.feedbackDetailsString(true);
    const email = this.sentToEmail?.email;
    this.emailString = `mailto:${email}?Subject=Arb Central ${this.feedbackType ? ': ' + this.feedbackType : ''} Feedback&body=${detailsForEmail}`;
    // }
    this.copyToClipboard(this.feedbackDetails);
  }

  cancel() {
    this.feedbackType = '';
    this.feedbackDetails = null;
    this.modalService.dismissAll();
    this.closeFeedback.emit();
  }

  feedbackDetailsString(emailFormat = false) {
    const token = emailFormat ? '%0A' : '';
    return `Feedback type: ${this.feedbackType}${token}
            Time: ${this.timeStamp}${token}
            URL: ${location.href}${token}
            Version: ${this.version}${token}
            Logged In Username: ${this.openOrder.userName}${token}
            User Branch: ${this.customerDetails.defaultBranch} - ${this.customerDetails.defaultBranchName}${token}
            Sales Order Number: ${this.openOrder.orderNumber}${token}
            Customer Number: ${this.openOrder.customerNumber}${token}
            POS Register ID: ${this.registerId}${token}
            JDE Vehicle ID: ${this.openOrder.partsVehicleID}${token}
            Customer Vehicle ID: ${this.openOrder.customerVehicleID}${token}
            Vehicle Description: ${this.openOrder.vehicleDescription}${token}
            ---------------------------------------------------------------
            Part Number:${token}
            Problem Description:${token}
                `;
  }

  get feedbackUrl(): string {
    if (!this.isMailtoLinkValid(this.emailString)) {
      if (!this.dialogServiceShown) {
        this.dialogServiceShown = true;
        this.dialogService
          .confirm(
            `Please manually send email to < ${this.sentToEmail?.email} >with the information saved to your clipboard.`,
            'No email client found to send email to support',
            'Copy to Clipboard',
            'OK',
            true,
            null,
            false
          )
          .pipe(takeUntil(this.unSubscribe$))
          .subscribe((result) => {
            this.dialogServiceShown = false;
            if (result) {
              this.copyToClipboard(this.feedbackDetails);
              this.toastr.success('Feedback details copied to clipboard. Please paste it in your email.');
              this.cancel();
            } else {
              this.cancel();
            }
          });
      }
    } else {
      return this.emailString;
    }
  }
  copyToClipboard(feedbackDetails: string) {
    if (!feedbackDetails) {
      return;
    }

    this.clipboardService.copy(this.feedbackDetails);
  }
  isMailtoLinkValid(link: string): boolean {
    return link.startsWith('mailto:') && link.includes('@');
  }

  get feedbackTitle(): string {
    return this.isExternalUser ? 'EMAIL TO ARB' : 'EMAIL TO HELPDESK';
  }

  openFeedbackInNativeBrowser(url: string) {
    this.electronService.openLinkNatively(url);
  }
}
